import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faImage, faVideo, faFileAudio, faFile } from '@fortawesome/free-solid-svg-icons';

/**
 * Component to display question/answer assets (images, videos, audio)
 * Handles different asset types and displays them appropriately
 * 
 * @param {Array} assets - Array of asset objects to display
 * @param {boolean} isLoading - Whether assets are currently loading
 * @param {string} error - Error message if asset loading failed
 * @param {boolean} isDarkMode - Whether dark mode is enabled
 * @param {Object} themeStyles - Theme styling object
 * @param {boolean} compact - If true, uses compact styling for inline display (e.g., within answers)
 * @param {string} label - Optional label to show above assets (e.g., "Answer Image", "Explanation Image")
 */
const AssetDisplay = ({ assets, isLoading, error, isDarkMode, themeStyles, compact = false, label = '' }) => {
  // Don't render anything if there are no assets
  if (!assets || assets.length === 0) {
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: compact ? '12px' : '20px',
        backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : 'rgba(248, 250, 252, 0.8)',
        borderRadius: '8px',
        marginBottom: compact ? '8px' : '16px'
      }}>
        <FontAwesomeIcon 
          icon={faSpinner} 
          spin 
          style={{ 
            marginRight: '8px',
            color: isDarkMode ? '#94a3b8' : '#64748b'
          }} 
        />
        <span style={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: compact ? '13px' : '14px' }}>
          Loading assets...
        </span>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{
        padding: compact ? '8px' : '12px',
        backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(220, 38, 38, 0.05)',
        color: isDarkMode ? '#ef4444' : '#dc2626',
        borderRadius: '8px',
        marginBottom: compact ? '8px' : '16px',
        fontSize: compact ? '12px' : '14px'
      }}>
        Failed to load assets: {error}
      </div>
    );
  }

  // Render asset based on type
  const renderAsset = (asset) => {
    const { type, url, name, mimeType } = asset;

    // Determine asset type from type field or mimeType
    const assetType = type || (mimeType?.startsWith('image/') ? 'Image' : 
                                mimeType?.startsWith('video/') ? 'Video' : 
                                mimeType?.startsWith('audio/') ? 'Audio' : 'Other');

    const containerStyle = {
      borderRadius: compact ? '6px' : '8px',
      overflow: 'hidden',
      backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : 'rgba(248, 250, 252, 0.8)',
      border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.05)',
      boxShadow: isDarkMode ? '0 2px 6px rgba(0, 0, 0, 0.1)' : '0 2px 6px rgba(0, 0, 0, 0.03)',
      height: '100%'
    };

    // Compact styling for images (used in answers)
    const maxImageHeight = compact ? '250px' : '400px';
    const maxVideoHeight = compact ? '250px' : '400px';

    switch (assetType) {
      case 'Image':
        return (
          <div key={asset.id} style={containerStyle}>
            <img 
              src={url} 
              alt={name || "Asset image"}
              style={{
                maxWidth: '100%',
                maxHeight: maxImageHeight,
                width: 'auto',
                height: 'auto',
                display: 'block',
                margin: '0 auto',
                objectFit: 'contain'
              }}
              onError={(e) => {
                console.error('Failed to load image:', url);
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = `
                  <div style="padding: 20px; text-align: center; color: ${isDarkMode ? '#ef4444' : '#dc2626'};">
                    <p>Failed to load image</p>
                  </div>
                `;
              }}
            />
          </div>
        );

      case 'Video':
        return (
          <div key={asset.id} style={containerStyle}>
            <video 
              controls
              style={{
                maxWidth: '100%',
                maxHeight: maxVideoHeight,
                width: '100%',
                display: 'block',
                backgroundColor: '#000'
              }}
            >
              <source src={url} type={mimeType || 'video/mp4'} />
              Your browser does not support the video tag.
            </video>
          </div>
        );

      case 'Audio':
        return (
          <div key={asset.id} style={containerStyle}>
            <div style={{ padding: '12px' }}>
              <audio 
                controls
                style={{
                  width: '100%',
                  maxWidth: '100%'
                }}
              >
                <source src={url} type={mimeType || 'audio/mpeg'} />
                Your browser does not support the audio tag.
              </audio>
            </div>
          </div>
        );

      default:
        // For other file types, show a download link
        return (
          <div key={asset.id} style={{
            ...containerStyle,
            padding: '12px'
          }}>
            <a 
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                color: isDarkMode ? '#60a5fa' : '#3b82f6',
                textDecoration: 'none',
                fontSize: '14px'
              }}
            >
              <FontAwesomeIcon icon={faFile} style={{ marginRight: '8px' }} />
              <span>Download file</span>
            </a>
          </div>
        );
    }
  };

  return (
    <div style={{
      marginTop: compact ? '8px' : '12px',
      marginBottom: compact ? '8px' : '16px',
      display: 'grid',
      gridTemplateColumns: compact ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: compact ? '8px' : '16px',
      width: '100%'
    }}>
      {label && (
        <div style={{
          gridColumn: '1 / -1',
          fontSize: compact ? '12px' : '14px',
          fontWeight: 500,
          color: isDarkMode ? '#94a3b8' : '#64748b',
          marginBottom: compact ? '4px' : '8px'
        }}>
          {label}
        </div>
      )}
      {assets.map(asset => renderAsset(asset))}
    </div>
  );
};

export default React.memo(AssetDisplay);
